/**
 * CampaignLanding — parameterized ad-campaign landing page.
 *
 * One component, many landing pages: each campaign is described by a JSON
 * file under `src/content/campaigns/<slug>.json` and rendered at `/lp/:slug`.
 * Spinning up a new ad-campaign LP is a JSON + (optional) hero-image change,
 * no code required.
 *
 * Each campaign declares which conversion event its primary CTA fires:
 *   - "Lead"                  → opens the ContactSalesModal (form submission
 *                                fires `Lead` from the modal itself)
 *   - "Schedule"              → opens the shared Cal.com demo booking flow
 *                                (booking confirmation fires `Schedule`)
 *   - "CompleteRegistration"  → fires `CompleteRegistration` here, then
 *                                navigates to /app
 *
 * Conversion events fired further down the funnel (modal submission) inherit
 * the captured UTM/click-ID context via `utm.ts`, so paid traffic clicked
 * to `/lp/<slug>?utm_source=meta&utm_campaign=…` is correctly attributed
 * even if the user takes several clicks to convert.
 */

import React, { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Hero, Features, CTA, Footer, SEO, PLAY_STORE_URL, APP_STORE_URL } from '@coupling/ui';
import Header from '../components/Header';
import { useBooking } from '../components/BookingProvider';
import ContactSalesModal from '../components/ContactSalesModal';
import PartnershipGrowthShowcase from '../components/marketing/PartnershipGrowthShowcase';
import { seoConfig } from '../config/seo';
import { analyticsService, AnalyticsEvent, ConversionEvent } from '../utils/analytics';

// ---- Campaign content registry ------------------------------------------
//
// Adding a new campaign:
//   1. Drop a JSON file in src/content/campaigns/<slug>.json (matching the
//      CampaignContent shape below).
//   2. Add an import + entry to CAMPAIGNS here.
//   3. (Optional) Drop a hero image in public/illustrations/ and reference it.

import wncMerchants from '../content/campaigns/wnc-merchants.json';
import posMode from '../content/campaigns/pos-mode.json';
import simplePricing from '../content/campaigns/simple-pricing.json';
import localMerchantNetwork from '../content/campaigns/local-merchant-network.json';

interface CampaignContent {
  slug: string;
  seo: { title: string; description: string };
  hero: {
    badge?: string;
    headline: string;
    description: string;
    primaryCta: string;
    primaryAction?: {
      type?: 'conversion' | 'scroll';
      targetId?: string;
    };
    image?: {
      src: string;
      alt: string;
      width?: number;
      height?: number;
      presentation?: 'default' | 'framed-cream';
    };
    background?: 'gradient' | 'solid' | 'subtle';
  };
  /** Which conversion event the primary CTA fires. */
  conversionEvent: 'Lead' | 'Schedule' | 'CompleteRegistration';
  partnershipShowcase?: {
    id: string;
    badge?: string;
    headline?: string;
    description?: string;
    primaryCta?: string;
  };
  features?: Array<{ icon: string; title: string; description: string }>;
  bottomCta?: {
    headline: string;
    description: string;
    primaryCta: string;
  };
}

const CAMPAIGNS: Record<string, CampaignContent> = {
  'wnc-merchants': wncMerchants as CampaignContent,
  'pos-mode': posMode as CampaignContent,
  'simple-pricing': simplePricing as CampaignContent,
  'local-merchant-network': localMerchantNetwork as CampaignContent,
};

// ---- Shared footer config -----------------------------------------------

const footerColumns = [
  {
    title: 'Product',
    links: [
      { label: 'Use Coupling your way', href: '/#product-modes' },
      { label: 'POS workflow', href: '/#pos-workflow' },
      { label: 'Pricing', href: '/#pricing' },
    ],
  },
  {
    title: 'Mobile App',
    links: [
      { label: 'Get the App', href: '/app' },
      { label: 'Google Play', href: PLAY_STORE_URL },
      { label: 'App Store', href: APP_STORE_URL },
    ],
  },
  {
    title: 'Network',
    links: [
      { label: 'Coupling (Global)', href: 'https://cplng.org' },
      { label: 'Blog', href: '/blog' },
    ],
  },
  {
    title: 'Legal',
    links: [{ label: 'Privacy Policy', href: '/privacy' }],
  },
];

const appUrl = import.meta.env.VITE_APP_URL || 'https://app.cplng.org';

// ---- Page component ------------------------------------------------------

const CampaignLanding: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [contactOpen, setContactOpen] = useState(false);
  const booking = useBooking();

  const campaign = slug ? CAMPAIGNS[slug] : undefined;

  // Unknown slug → fall through to NotFound. Using Navigate keeps the user's
  // ad-click visible in analytics as a 404 if the campaign was misconfigured.
  if (!campaign) {
    return <Navigate to="/404" replace />;
  }

  /**
   * Primary-CTA click handler. Routes the user to the correct conversion
   * surface for the campaign's declared event:
   *   Lead → ContactSalesModal
   *   Schedule → Cal.com demo booking modal
   *   CompleteRegistration → /app (and fires the conversion immediately,
   *                                 since downstream signup happens off-site)
   */
  const openConversionCta = (ctaName: string) => {
    analyticsService.logEvent(AnalyticsEvent.CTA_CLICK, {
      cta_name: ctaName,
      campaign_slug: campaign.slug,
      conversion_event: campaign.conversionEvent,
    });

    switch (campaign.conversionEvent) {
      case 'Lead':
        setContactOpen(true);
        break;
      case 'Schedule':
        // Keep Schedule as a confirmed-booking conversion, not a CTA-click
        // intent. The click itself is logged above; BookingModal emits the
        // Schedule conversion after Cal.com confirms the appointment.
        booking.open('demo');
        break;
      case 'CompleteRegistration':
        // Off-site signup completion is out of this site's scope, so fire
        // the conversion on intent here. Mirrors the Header "Get the App"
        // CTA — see the note there for the long-term direction.
        analyticsService.logConversion(ConversionEvent.COMPLETE_REGISTRATION, {
          content_name: 'campaign_lp_signup',
          campaign_slug: campaign.slug,
        });
        window.open(`${appUrl}/signup`, '_blank', 'noopener,noreferrer');
        break;
    }
  };

  const handlePrimaryCta = () => {
    if (campaign.hero.primaryAction?.type === 'scroll') {
      analyticsService.logEvent(AnalyticsEvent.CTA_CLICK, {
        cta_name: 'lp_primary_scroll',
        campaign_slug: campaign.slug,
        cta_action: 'scroll',
        scroll_target: campaign.hero.primaryAction.targetId,
      });

      if (campaign.hero.primaryAction.targetId) {
        document
          .getElementById(campaign.hero.primaryAction.targetId)
          ?.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }

    openConversionCta('lp_primary');
  };

  const canonical = `${seoConfig.siteUrl}/lp/${campaign.slug}`;
  const heroImage = campaign.hero.image ? (
    campaign.hero.image.presentation === 'framed-cream' ? (
      <div className="rounded-[2.4rem] border border-[#efd8bf] bg-[linear-gradient(180deg,#fffaf3_0%,#f5e7d5_100%)] p-2 shadow-[0_34px_80px_rgba(70,39,21,0.18)]">
        <div className="rounded-[1.9rem] border border-white/70 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.94),rgba(255,248,238,0.72))] p-2 sm:p-2.5">
          <img
            src={campaign.hero.image.src}
            alt={campaign.hero.image.alt}
            className="aspect-[1.16/1] w-full rounded-[1.4rem] object-cover object-center shadow-[0_26px_60px_rgba(71,39,21,0.16)]"
            width={campaign.hero.image.width ?? 1400}
            height={campaign.hero.image.height ?? 1000}
          />
        </div>
      </div>
    ) : (
      <img
        src={campaign.hero.image.src}
        alt={campaign.hero.image.alt}
        className="w-full h-auto rounded-2xl"
        width={campaign.hero.image.width ?? 1400}
        height={campaign.hero.image.height ?? 1000}
      />
    )
  ) : undefined;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={campaign.seo.title}
        description={campaign.seo.description}
        canonical={canonical}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={canonical}
      />
      <Header />

      <Hero
        badge={campaign.hero.badge}
        headline={campaign.hero.headline}
        description={campaign.hero.description}
        primaryCta={campaign.hero.primaryCta}
        onPrimaryCta={handlePrimaryCta}
        image={heroImage}
        background={campaign.hero.background ?? 'gradient'}
      />

      {campaign.partnershipShowcase && (
        <section className="bg-[#fffaf4] px-4 py-16">
          <div className="mx-auto max-w-6xl">
            <PartnershipGrowthShowcase
              id={campaign.partnershipShowcase.id}
              badge={campaign.partnershipShowcase.badge}
              headline={campaign.partnershipShowcase.headline}
              description={campaign.partnershipShowcase.description}
              titleLevel="h2"
              primaryCta={campaign.partnershipShowcase.primaryCta}
              onPrimaryCta={() => openConversionCta('lp_partnership_showcase')}
            />
          </div>
        </section>
      )}

      {campaign.features && campaign.features.length > 0 && (
        <section id="lp-features" className="py-24 px-4 bg-white scroll-mt-24">
          <div className="max-w-6xl mx-auto">
            <Features features={campaign.features} columns={3} />
          </div>
        </section>
      )}

      {campaign.bottomCta && (
        <CTA
          headline={campaign.bottomCta.headline}
          description={campaign.bottomCta.description}
          primaryCta={campaign.bottomCta.primaryCta}
          onPrimaryCta={() => openConversionCta('lp_bottom')}
          background="gradient"
          size="lg"
          align="center"
        />
      )}

      <Footer
        description="Helping WNC merchants create interconnected reward networks that strengthen mountain communities."
        columns={footerColumns}
        copyright={`© ${new Date().getFullYear()} Coupling WNC. Part of the Coupling network.`}
      />

      <ContactSalesModal
        isOpen={contactOpen}
        onClose={() => setContactOpen(false)}
      />
    </div>
  );
};

export default CampaignLanding;
